import { IconButton, Drawer, Stack, Typography, Divider, Box, Alert, Button } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import _isEqual from 'lodash/isEqual';
import { TSceneComponent, TourLoaderEventsType, iTHREE } from '@treedis/tour-loader';

import { toast } from 'react-toastify';
import DeviceManager from '@/components/util/deviceManager';
import { ThreadLoginPopup } from './components/modals/ThreadLoginPopup';
import getTokenData from '@/utils/getTokenData';
import socket from '@/components/util/socket';
import { formData } from '@/utils/formData';
import PublicAPI from '@/api/TreedisPublicDAO';
import { EditMenu } from './components/EditMenu';
import Icons from '@/units/Icon';
import { AddWorkflow, NoteAction, TNote, TNoteMessage } from './types';
import { BottomBar } from './components/ui/BottomBar';
import { AnimatedOverlay } from '@/components/common/AnimatedOverlay';
import { Board } from './components/board';
import { AddNoteMode, NoteIntegration, SidebarStep } from './enums';
import { NotesContext } from './context';
import { useNotesContext } from './hooks/useNotesContext';
import { Tour } from '@/_models/Tour';
import { AnimatedContentSwitch } from '@/components/common/AnimatedContentSwitch';
import { NotesList } from './components/notes-list';
import { useAddNote } from './hooks/useAddNote';
import { Note } from './components/note';
import { NotesBoardProvider } from './components/board/context';
import { DeleteNoteModal } from './components/modals/DeleteNoteModal';
import { CompleteNoteModal } from './components/modals/CompleteNoteModal';
import { Tag } from '@/_models/Tag';
import { FAIcon } from '@packages/ui-components';

import { useNotesStore } from './stores/notes.store';

export enum NoteEditEventType {
  EditPosition = 'editPosition',
  EditComplition = 'editComplition',
}

export const NotesContent = () => {
  const {
    appState,
    addThreadToSpace,
    showcaseTag,
    selectedThreadProp,
    incrementNewMessagesCount,
    decrementNewMessagesCount,
    resetNewMessagesCount,
    removeThreadFromSpace,
    toggleThreadVisibility,
    hideElements,
    initialThreads,
    setSidebarStep,
    sidebarStep,
    tour,
    isOpen,
    closeDrawer,
    openDrawer,
    showCaseTemp,
    isGuest,
    setIsGuest,
    userId,
    newThread,
    isBoardOpen,
    setIsBoardOpen,
    setMediaUrls,
    attachToCursor,
    setNewThreadMessages,
    controls,
    tagNode,
    normal,
    contentDrawerRef,
    refUserId,
    initialThreadsRef,
    updateMessages,
    handleCompleteNote,
    handleMoveToNote,
    setNoteToEdit,
    noteToEdit,
    addingWorkflow,
    setAddingWorkflow,
    handleQuitAdding,
    selectedFilterTag,
    shouldShowCollaboratorsLogin,
  } = useNotesContext();

  const { deleteNote, setNotes, selectNote, deleteMessage } = useNotesStore(
    (state) => state.actions,
  );
  const notes = useNotesStore((state) => state.notes);
  const selectedNote = useNotesStore((state) => state.selectedNote);
  const { handleAddNote } = useAddNote();

  const isNewThread = useMemo(
    () => !!newThread || !!addingWorkflow?.active,
    [newThread, addingWorkflow],
  );

  useEffect(() => {
    if (isBoardOpen && !tour.notesTaskManagerModeEnabled) {
      setIsBoardOpen(false);
    }
  }, [isBoardOpen, tour.notesTaskManagerModeEnabled]);

  useEffect(() => {
    if (addingWorkflow?.mode) {
      setSidebarStep(SidebarStep.Note);
    }
  }, [addingWorkflow, setSidebarStep]);

  const handleIncrementNewMessagesCount = useCallback(
    (newThreadMessageUserId: number, newThreadMessageThreadId: number) => {
      if (newThreadMessageUserId === refUserId.current) return;
      incrementNewMessagesCount();
      setNewThreadMessages((prevMessages) => {
        // Create a new object to avoid mutating the previous state
        const newMessages = { ...prevMessages };
        if (newMessages[newThreadMessageThreadId]) {
          newMessages[newThreadMessageThreadId] += 1;
        } else {
          newMessages[newThreadMessageThreadId] = 1;
        }
        return newMessages;
      });
    },
    [incrementNewMessagesCount, refUserId, setNewThreadMessages],
  );

  const handleDecrementNewMessagesCount = useCallback(
    (newThreadMessageUserId: number, newThreadMessageThreadId: number) => {
      if (newThreadMessageUserId === refUserId.current) return;
      decrementNewMessagesCount();
      setNewThreadMessages((prevMessages) => {
        // Create a new object to avoid mutating the previous state
        const newMessages = { ...prevMessages };
        if (newMessages[newThreadMessageThreadId]) {
          newMessages[newThreadMessageThreadId] -= 1;
        }
        return newMessages;
      });
    },
    [decrementNewMessagesCount, refUserId, setNewThreadMessages],
  );

  const backToThreadsStep = useCallback(() => {
    setSidebarStep(SidebarStep.NotesList);
    selectNote(null);
    setAddingWorkflow(null);
    setNoteToEdit(null);

    if (contentDrawerRef?.current?.scrollTo) {
      contentDrawerRef.current.scrollTo(0, 0);
    }
  }, [setSidebarStep, selectNote, setNoteToEdit, contentDrawerRef]);

  useEffect(() => {
    // Setup socket event handlers
    const setupSocketHandlers = () => {
      socket.emit('thread_join', { room: tour.slug });

      // Handler for thread_added event
      const handleThreadAdded = async (data) => {
        const { newThread: threadFromAnotherUser } = data;
        threadFromAnotherUser.customIcon = 'thread';
        await addThreadToSpace(threadFromAnotherUser);
        setNotes((prevThreads) => [...prevThreads, threadFromAnotherUser]);
        incrementNewMessagesCount();
      };

      // Handler for thread_edit event
      const handleThreadEdit = async (data) => {
        const { savedThread, eventType } = data;

        if (eventType === NoteEditEventType.EditPosition) {
          if (data.isRecentNote) {
            await showCaseTemp.tag.edit(savedThread);
          } else {
            // Find the thread that matches any of the conditions
            const foundThread = initialThreadsRef.current.find(
              (thread) =>
                thread.defaultSid === savedThread.defaultSid ||
                thread.sid === savedThread.defaultSid ||
                thread.defaultSid === savedThread.sid ||
                thread.id === savedThread.id,
            );
            if (foundThread?.sid) {
              await showcaseTag.editPosition(foundThread.sid, {
                anchorPosition: {
                  x: savedThread.positionX,
                  y: savedThread.positionY,
                  z: savedThread.positionZ,
                },
              });

              setNotes((pvevNotes) => {
                return pvevNotes.map((thread) => {
                  if (thread.id === savedThread.id) {
                    return {
                      ...thread,
                      positionX: savedThread.positionX,
                      positionY: savedThread.positionY,
                      positionZ: savedThread.positionZ,
                    };
                  }
                  return thread;
                });
              });
            }
          }
        }

        if (eventType === NoteEditEventType.EditComplition) {
          await handleCompleteNote(savedThread.id, savedThread);
        }
      };

      // Handler for message_added event
      const handleMessageAdded = (data) => {
        let updatedThread;
        setNotes((prevNotes) => {
          const foundThread = prevNotes.find(
            (thread) => thread.id === data.newThreadMessage.threadId,
          );
          if (foundThread) {
            return prevNotes.map((thread) => {
              if (thread.id === foundThread.id) {
                const messages = updateMessages(thread, data.newThreadMessage);
                updatedThread = { ...thread, messages };
                return updatedThread;
              }
              return thread;
            });
          }
          return prevNotes;
        });

        handleIncrementNewMessagesCount(
          data.newThreadMessage.userId,
          data.newThreadMessage.threadId,
        );
      };

      // Handler for message_edited event
      const handleMessageEdited = (data) => {
        let updatedThread;
        setNotes((prevThreads) => {
          const foundThread = prevThreads.find(
            (thread) => thread.id === data.updatedMessage.threadId,
          );
          if (foundThread) {
            return prevThreads.map((thread) => {
              if (thread.id === foundThread.id) {
                const messages = updateMessages(thread, data.updatedMessage);
                updatedThread = { ...thread, title: data.updatedMessage.savedTitle, messages };
                return updatedThread;
              }
              return thread;
            });
          }
          return prevThreads;
        });
      };

      // Handler for message_deleted event
      const handleMessageDeleted = (data) => {
        deleteMessage(data.data.threadId, data.data.id);

        handleDecrementNewMessagesCount(data.data.userId, data.data.threadId);
      };

      // Handler for thread_delete event
      const handleThreadDelete = async (data) => {
        const { deletedThread } = data;
        await removeThreadFromSpace(deletedThread);
        deleteNote(deletedThread.id);
        backToThreadsStep();
      };

      // Register all event handlers
      socket.on('thread_added', handleThreadAdded);
      socket.on('thread_edit', handleThreadEdit);
      socket.on('message_added', handleMessageAdded);
      socket.on('message_edited', handleMessageEdited);
      socket.on('message_deleted', handleMessageDeleted);
      socket.on('thread_delete', handleThreadDelete);

      // Return cleanup function to remove all event listeners
      return () => {
        socket.off('thread_added', handleThreadAdded);
        socket.off('thread_edit', handleThreadEdit);
        socket.off('message_added', handleMessageAdded);
        socket.off('message_edited', handleMessageEdited);
        socket.off('message_deleted', handleMessageDeleted);
        socket.off('thread_delete', handleThreadDelete);
      };
    };

    // Setup handlers and store cleanup function
    const cleanup = setupSocketHandlers();

    // Return cleanup function to useEffect
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // find clicked thread from tag click
  useEffect(() => {
    if (selectedThreadProp?.id) selectNote(selectedThreadProp.id);
  }, [selectedThreadProp, notes, selectNote]);

  const configureScene = useCallback(async () => {
    await showCaseTemp.tag.configureScene(controls);

    showCaseTemp.tag.pointerIntersection(tagNode, attachToCursor);
  }, [showCaseTemp.tag, controls, tagNode, attachToCursor]);

  useEffect(() => {
    if (appState === TourLoaderEventsType.PLAYING) {
      configureScene();
    }
  }, [appState, configureScene]);

  useEffect(() => {
    refUserId.current = userId;
  }, [userId, refUserId]);

  useEffect(() => {
    const getThree = async () => {
      const THREE = await iTHREE();
      normal.current = new THREE.Vector3(0, 1, 0);
    };

    getThree();
  }, [normal]);

  const handleCloseDrawer = useCallback(async () => {
    handleQuitAdding();

    closeDrawer();
  }, [handleQuitAdding, closeDrawer]);

  useEffect(() => {
    if (selectedNote || newThread || !!addingWorkflow?.mode) {
      setSidebarStep(SidebarStep.Note);
    } else {
      setSidebarStep(SidebarStep.NotesList);
    }

    return () => {
      setSidebarStep(SidebarStep.NotesList);
    };
  }, [newThread, selectedNote, addingWorkflow, setSidebarStep]);

  const handleThreadItemSelect = useCallback(
    (thread) => {
      selectNote(thread.id);
      setSidebarStep(SidebarStep.Note);
      setNewThreadMessages((prevMessages) => ({ ...prevMessages, [thread.id]: 0 }));
      resetNewMessagesCount();
    },
    [selectNote, setSidebarStep, setNewThreadMessages, resetNewMessagesCount],
  );

  const mainMessage = useMemo(() => {
    return selectedNote?.messages?.find((message) => message?.isMainMessage) || null;
  }, [selectedNote?.messages]);

  useEffect(() => {
    const keyDownHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleQuitAdding();
      }
    };

    if (addingWorkflow?.active && addingWorkflow?.mode === AddNoteMode.Temporary) {
      document.addEventListener('keydown', keyDownHandler);
    } else {
      document.removeEventListener('keydown', keyDownHandler);
    }

    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [addingWorkflow, handleQuitAdding]);

  useEffect(() => {
    if (newThread) {
      openDrawer();
    }
  }, [newThread, openDrawer]);

  useEffect(() => {
    if (isGuest) {
      hideElements({
        hideTags: initialThreads.length ? initialThreads.map((t) => t.sid) : [],
        polygonsToHide: [],
        vsImagesToHide: [],
        vsVideosToHide: [],
        vsItemsToHide: [],
      });
    }
  }, [isGuest, initialThreads, hideElements]);

  const direction = useMemo(() => (tour.client?.rtl ? 'rtl' : ''), [tour.client?.rtl]);

  return (
    <>
      {!isGuest && (
        <Stack className="no-reset no-reset-list">
          <Drawer
            open={isOpen}
            anchor="right"
            variant="persistent"
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              sx: {
                maxWidth: DeviceManager.isMobile() || DeviceManager.isTablet() ? '394px' : '394px',
                width: '394px',
                background: 'transparent',
                overflowX: 'hidden',
                flexDirection: 'row-reverse',
                alignItems: 'center',
                border: 'none',
              },
            }}
            sx={{
              flexDirection: 'row-reverse',
              alignItems: 'center',
            }}
          >
            <Stack
              sx={{
                height: '100%',
                background: '#F7F7F7',
                minWidth: DeviceManager.isMobile() || DeviceManager.isTablet() ? '394px' : '394px',
                width: '394px',
                borderLeft: '1px solid #EAECF0',
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                p="14px 10px 14px 18px"
                sx={{ direction, background: 'white', minHeight: '56px' }}
              >
                <Stack direction="row" gap="8px" alignItems="center">
                  {sidebarStep === SidebarStep.Note && !addingWorkflow?.active && (
                    <IconButton
                      onClick={backToThreadsStep}
                      sx={{ gap: 1, width: '24px', height: '24px' }}
                    >
                      {direction ? (
                        <FAIcon icon="faChevronRight" size={18} />
                      ) : (
                        <FAIcon icon="faChevronLeft" size={18} color="#101828" />
                      )}
                    </IconButton>
                  )}
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 'bold', fontSize: '18px', font: 'Inter' }}
                  >
                    {(() => {
                      if (sidebarStep === SidebarStep.NotesList) return <>Notes</>;
                      if (isNewThread) return 'New note';
                      return '';
                    })()}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing="16px"
                >
                  {tour.notesTaskManagerModeEnabled && sidebarStep === SidebarStep.NotesList ? (
                    <IconButton
                      sx={{ width: '24px', height: '24px' }}
                      onClick={() => setIsBoardOpen(true)}
                    >
                      <Icons.ExpandArrows style={{ fontSize: '18px' }} />
                    </IconButton>
                  ) : null}

                  {sidebarStep === SidebarStep.Note && selectedNote && (
                    <IconButton
                      onClick={() => handleMoveToNote(selectedNote)}
                      id={`target-button-${selectedNote?.id}`}
                      sx={{ width: '24px', height: '24px' }}
                    >
                      <FAIcon icon="faLocationCrosshairs" size={18} color="#101828" />
                    </IconButton>
                  )}
                  {sidebarStep === SidebarStep.Note &&
                    selectedNote &&
                    mainMessage?.userId === userId && <EditMenu message={mainMessage} />}
                  <IconButton onClick={handleCloseDrawer} sx={{ width: '24px', height: '24px' }}>
                    <FAIcon icon="faXmarkLarge" size={18} color="#101828" />
                  </IconButton>
                </Stack>
              </Stack>
              <Divider />

              <AnimatedContentSwitch
                mode={sidebarStep}
                orderedModes={[SidebarStep.NotesList, SidebarStep.Note]}
                contentVariants={{
                  list: (
                    <Box
                      sx={{
                        overflow: 'auto',
                        height: '100%',
                        '&::-webkit-scrollbar': {
                          display: 'none',
                        },
                        /* IE and Edge */
                        msOverflowStyle: 'none',
                        /* Firefox */
                        scrollbarWidth: 'none',
                      }}
                    >
                      <NotesList
                        onAddNote={handleAddNote}
                        onNoteClick={(note) => {
                          handleThreadItemSelect?.(note);
                        }}
                        onEditNote={(note) => {
                          const message = note.messages.find((m) => m.isMainMessage);
                          if (!message) return;

                          handleThreadItemSelect?.(note);
                          setMediaUrls(
                            message.mediaUrls.map((url) => ({ src: url, flaggedToDelete: false })),
                          );
                          setNoteToEdit({ note, message });
                        }}
                      />
                    </Box>
                  ),
                  note: (
                    <Box
                      sx={{
                        overflow: 'auto',
                        height: '100%',
                        '&::-webkit-scrollbar': {
                          display: 'none',
                        },
                        /* IE and Edge */
                        msOverflowStyle: 'none',
                        /* Firefox */
                        scrollbarWidth: 'none',
                      }}
                    >
                      <Note
                        onNewNoteCancel={handleCloseDrawer}
                        onNoteCreated={(savedNote) => {
                          handleThreadItemSelect(savedNote);
                        }}
                      />
                    </Box>
                  ),
                }}
              />

              {!isGuest && !selectedNote && sidebarStep === SidebarStep.NotesList ? (
                <BottomBar sx={{ justifyContent: 'center' }}>
                  <Button
                    disabled={addingWorkflow?.active || !!noteToEdit || isGuest}
                    variant="contained"
                    startIcon={<Icons.Add />}
                    sx={{
                      backgroundColor: '#2055FF',
                      color: '#ffffff',
                      borderRadius: '8px',
                      padding: '8px 16px',
                      width: '200px',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      boxShadow: '0px 1px 2px 0px #1018280D',
                      '& .MuiButton-startIcon': {
                        marginRight: '8px',
                      },
                    }}
                    onClick={() => {
                      if (selectedFilterTag) {
                        setAddingWorkflow({
                          active: true,
                          mode: AddNoteMode.Tag,
                          tag: selectedFilterTag,
                          isTagStatic: true,
                        });
                      } else {
                        handleAddNote();
                      }
                    }}
                  >
                    {selectedFilterTag ? 'Add note to tag' : 'Add note'}
                  </Button>
                </BottomBar>
              ) : null}
            </Stack>

            <Box
              sx={{
                top: '50%',
                left: 0,
                zIndex: 9999,
              }}
            >
              <IconButton
                onClick={closeDrawer}
                sx={{
                  width: '20px',
                  height: '80px',
                  background: '#ffffff',
                  borderRadius: 0,
                  borderTopLeftRadius: '8px',
                  borderBottomLeftRadius: '8px',
                  '&:hover': {
                    background: '#f0f0f0',
                  },
                }}
              >
                <FAIcon icon="faChevronRight" size={16} color="#000000" />
              </IconButton>
            </Box>
          </Drawer>
          <Drawer
            open={!isOpen}
            anchor="right"
            variant="persistent"
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              sx: {
                maxWidth: DeviceManager.isMobile() || DeviceManager.isTablet() ? '416px' : '494px',
                background: 'transparent',
                overflowX: 'hidden',
                overflowY: 'scroll',
                flexDirection: 'row-reverse',
                alignItems: 'center',
                border: 'none',
              },
            }}
            sx={{
              flexDirection: 'row-reverse',
              alignItems: 'center',
            }}
          >
            <div>
              <IconButton
                sx={{
                  right: 0,
                  width: '20px',
                  height: '80px',
                  background: '#ffffff',
                  borderRadius: 0,
                  borderTopLeftRadius: '8px',
                  borderBottomLeftRadius: '8px',
                  '&:hover': {
                    background: '#f0f0f0',
                  },
                }}
                onClick={openDrawer}
              >
                <FAIcon icon="faChevronLeft" size={16} color="#000000" />
              </IconButton>
            </div>
          </Drawer>
        </Stack>
      )}

      <DeleteNoteModal />
      <CompleteNoteModal completeNote={handleCompleteNote} />

      {!tour?.enableIot && !shouldShowCollaboratorsLogin && <ThreadLoginPopup />}

      {addingWorkflow?.active && addingWorkflow?.mode === AddNoteMode.Temporary && (
        <Alert
          severity="info"
          sx={{
            zIndex: 9999,
            position: 'fixed',
            top: 10,
            left: 0,
            right: 0,
            py: 0,
            mx: 'auto',
            maxWidth: '230px',
            justifyContent: 'center',
            background: 'rgba(0, 0, 0, 0.4)',
            transition: 'all 0.3s ease',
            backdropFilter: 'blur(15px)',
          }}
        >
          <Typography
            color="primary.main"
            sx={{
              color: '#fff',
            }}
          >
            press Esc to cancel
          </Typography>
        </Alert>
      )}

      <NotesBoardProvider
        onClose={() => {
          handleCloseDrawer();
          setIsBoardOpen(false);
        }}
        onShrink={() => setIsBoardOpen(false)}
      >
        <AnimatedOverlay open={isBoardOpen}>
          <Board />
        </AnimatedOverlay>
      </NotesBoardProvider>
    </>
  );
};

export type NotesProps = {
  tags: any[];
  isOpen: boolean;
  closeDrawer: () => void;
  openDrawer: () => void;
  showCaseTemp: any;
  iframe: any;
  tour: Tour;
  initialThreads: any;
  appState: any;
  addThreadToSpace: (note: any) => Promise<any>;
  showcase: any;
  showcaseTag: any;
  selectedThreadProp: any;
  incrementNewMessagesCount: () => void;
  decrementNewMessagesCount: () => void;
  resetNewMessagesCount: () => void;
  removeThreadFromSpace: (thread: any) => Promise<void>;
  toggleThreadVisibility: (thread: any, isVisible: boolean) => void;
  hideElements: (data: any) => Promise<void>;
  getLocation: () => string;
  isRtl: boolean;
  moveToTag: (tagSid: string, openTag?: boolean) => void;
  selectedNoteTag: Tag | null;
  unselectNoteTag: () => void;
  shouldShowCollaboratorsLogin: boolean;
  passwordAuthenticated: boolean;
};

export function Notes({ ...props }: NotesProps) {
  const {
    tour,
    initialThreads,
    moveToTag,
    tags,
    showCaseTemp,
    iframe,
    selectedNoteTag,
    unselectNoteTag,
    isOpen,
  } = props;

  const [sidebarStep, setSidebarStep] = useState<SidebarStep>(
    selectedNoteTag ? SidebarStep.Note : SidebarStep.NotesList,
  );
  const [collaborators, setCollaborators] = useState(tour?.users);
  const [userId, setUserId] = useState(null);

  const [isGuest, setIsGuest] = useState(() => {
    const guestData = localStorage.getItem('treedisGuest');
    if (!guestData) return false;
    try {
      return JSON.parse(guestData)?.isGuest ?? false;
    } catch (e) {
      console.warn('Invalid guest data in localStorage', e);
      return false;
    }
  });
  const initialThreadsRef = useRef<any>([]);

  const { updateNote, setNotes, selectNote } = useNotesStore((state) => state.actions);
  const notes = useNotesStore((state) => state.notes);

  useEffect(() => {
    if (_isEqual(initialThreads, initialThreadsRef.current)) return;

    //NOTE: Remove duplicate notes based on position ( issue with duplicated tags init)
    const uniqueNotes = removeDuplicateNotesByPosition(initialThreads);

    initialThreadsRef.current = [...uniqueNotes];
    setNotes([...uniqueNotes]);
  }, [initialThreads, setNotes]);

  const removeDuplicateNotesByPosition = (notes) => {
    const uniquePositions = new Map();
    return notes.filter((note) => {
      if (!note.positionX || !note.positionY || !note.positionZ || note.tagId) return true;
      const posKey = `${Math.round(note.positionX * 100) / 100},${
        Math.round(note.positionY * 100) / 100
      },${Math.round(note.positionZ * 100) / 100}`;
      if (uniquePositions.has(posKey)) {
        return false;
      }
      uniquePositions.set(posKey, true);
      return true;
    });
  };

  const integration: NoteIntegration = useMemo(() => {
    if (tour.activeNotesIntegration) {
      return tour.activeNotesIntegration;
    }

    return NoteIntegration.None;
  }, [tour]);
  const isTourAdmin = useMemo(() => tour.userId === userId, [tour.userId, userId]);
  const [isBoardOpen, setIsBoardOpen] = useState(false);
  const [mediaUrls, setMediaUrls] = useState<{ src: string; flaggedToDelete: boolean }[]>([]);

  // Defines user selected way of adding note
  const [addingWorkflow, setAddingWorkflow] = useState<AddWorkflow | null>(null);
  const [newThread, setNewThread] = useState(null);

  // Defines note or note message to edit
  const [noteToEdit, setNoteToEdit] = useState<{ note: TNote; message?: TNoteMessage } | null>(
    null,
  );

  // Defines current action to perform on a note (delete or change note status)
  const [currentAction, setCurrentAction] = useState<NoteAction | null>(null);

  const [newThreadMessages, setNewThreadMessages] = useState<{ [key: number]: number }>({});
  const controls = useRef<TSceneComponent | null>(null);
  const tagNode = useRef<any>();
  const inputNodeComponent = useRef<any>();
  const attachToCursor = useRef<any>(false);
  const normal = useRef<any>();
  const clickListenerRef = useRef<any>();
  const contentDrawerRef = useRef<any>();
  const refUserId = useRef<number>();

  // Single tag selected in sidebar filters
  const [selectedFilterTag, setSelectedFilterTag] = useState<Tag | null>(null);

  const handleQuitAdding = useCallback(async () => {
    if (controls.current?.inputs) {
      controls.current.inputs.selection = null;
    }

    if (newThread) {
      await showCaseTemp.tag.remove(newThread.sid);
      setNewThread(null);
    } else {
      if (tagNode.current) {
        tagNode.current.stop();
        tagNode.current = undefined;
      }
      if (inputNodeComponent.current) {
        inputNodeComponent.current = undefined;
      }
      if (clickListenerRef?.current) {
        iframe.contentWindow.document.removeEventListener('click', clickListenerRef.current);
        clickListenerRef.current = null;
      }
    }

    selectNote(null);
    setNoteToEdit(null);
    setSidebarStep(SidebarStep.NotesList);
    setAddingWorkflow(null);
  }, [
    controls,
    newThread,
    setSidebarStep,
    showCaseTemp.tag,
    setNewThread,
    tagNode,
    inputNodeComponent,
    clickListenerRef,
    iframe,
    selectNote,
    setNoteToEdit,
    setAddingWorkflow,
  ]);

  // Reset selected tag when drawer is closed
  useEffect(() => {
    if (!isOpen) {
      unselectNoteTag();
    }
  }, [isOpen, unselectNoteTag]);

  // Reset selected tag when board is opened
  useEffect(() => {
    if (isBoardOpen) {
      unselectNoteTag();
    }
  }, [isBoardOpen, unselectNoteTag]);

  useEffect(() => {
    if (selectedNoteTag) {
      selectNote(null);
      setSidebarStep(SidebarStep.NotesList);
    }
  }, [selectedNoteTag, selectNote]);

  const updateMessages = useCallback((thread, messageToUpdate) => {
    const { messages } = thread;
    const messageIndex = messages.findIndex((message) => message?.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      messages[messageIndex] = messageToUpdate;
    } else {
      messages.push(messageToUpdate);
    }

    return messages;
  }, []);

  const handleMoveToNote = useCallback(
    (thread: TNote) => {
      if (isBoardOpen) {
        setIsBoardOpen(false);
      }

      const noteTag = tags.find((t) => t.id === thread.tagId);

      if (noteTag) {
        moveToTag(noteTag.sid, false);
        return;
      }

      moveToTag(thread.sid, false);
    },
    [isBoardOpen, tags, moveToTag],
  );

  const handleCompleteNote = async (noteId, noteFromAnotherUser) => {
    const thread = notes.find((t) => t.id === noteId);

    if (!noteFromAnotherUser) {
      // @ts-ignore
      delete thread.message;
      delete thread.dummyObject;

      const dataToSave = formData.create({
        ...thread,
        isCompleted: !thread.isCompleted,
      });
      try {
        const result = await PublicAPI.saveThread({
          data: dataToSave,
        });
        if (result.message === 'Success') {
          const { savedThread } = result;
          updateNote(savedThread.id, { isCompleted: savedThread.isCompleted });

          toast.success(`Note marked as ${savedThread.isCompleted ? 'completed' : 'incomplete'}`);
          socket.emit('thread_edit', {
            ...savedThread,
            room: tour.slug,
            eventType: NoteEditEventType.EditComplition,
          });
        }
      } catch (error) {
        toast.error(`Failed to mark Note as ${!thread.isCompleted ? 'completed' : 'incomplete'}`);
      }
    } else {
      setNotes((prevNotes) => {
        return prevNotes.map((t) =>
          t.id === noteFromAnotherUser?.id
            ? { ...t, isCompleted: noteFromAnotherUser?.isCompleted }
            : t,
        );
      });
    }
  };

  useEffect(() => {
    setCollaborators(tour.users);

    const guestData = localStorage.getItem('treedisGuest');
    if (guestData) {
      try {
        const isGuestFromStorage = JSON.parse(guestData)?.isGuest ?? false;
        setIsGuest(isGuestFromStorage);
      } catch (e) {
        console.warn('Invalid guest data in localStorage', e);
        setIsGuest(false);
      }
    } else {
      setIsGuest(false);
    }
  }, [tour]);

  useEffect(() => {
    const token = localStorage.getItem('publicUserToken');
    if (token) {
      const { userIdFromToken } = getTokenData(token);
      if (!userIdFromToken) {
        setUserId(null);
      }
      setUserId(Number(userIdFromToken));
    }
  }, [userId, setUserId]);

  return (
    <NotesContext.Provider
      value={{
        ...props,
        sidebarStep,
        setSidebarStep,
        collaborators,
        isGuest,
        setIsGuest,
        userId,
        setUserId,
        newThread,
        setNewThread,
        integration,
        isTourAdmin,
        isBoardOpen,
        setIsBoardOpen,
        mediaUrls,
        setMediaUrls,
        newThreadMessages,
        setNewThreadMessages,
        controls,
        tagNode,
        inputNodeComponent,
        attachToCursor,
        normal,
        clickListenerRef,
        contentDrawerRef,
        refUserId,
        initialThreadsRef,
        updateMessages,
        handleCompleteNote,
        currentAction,
        setCurrentAction,
        noteToEdit,
        setNoteToEdit,
        handleMoveToNote,
        addingWorkflow,
        setAddingWorkflow,
        handleQuitAdding,
        selectedFilterTag,
        setSelectedFilterTag,
      }}
    >
      <NotesContent />
    </NotesContext.Provider>
  );
}
